import React from 'react';
import { FormGroup, Label, Input } from '../../../styles/components';
import { DocumentArrowUpIcon } from '@heroicons/react/24/outline';
import styled from 'styled-components';

const FileUploadArea = styled.div<{ $isDragOver: boolean }>`
  border: 2px dashed ${props => props.$isDragOver 
    ? props.theme.colors.primary[400] 
    : props.theme.colors.gray[300]};
  border-radius: ${props => props.theme.borders.radius.lg};
  padding: ${props => props.theme.spacing.xl};
  text-align: center;
  background-color: ${props => props.$isDragOver 
    ? props.theme.colors.primary[50] 
    : props.theme.colors.gray[50]};
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary[400]};
    background-color: ${props => props.theme.colors.primary[50]};
  }
`;

const FileInput = styled.input`
  display: none;
`;

interface CertificateStepProps {
  certificate: File | null;
  certificatePassword: string;
  onCertificateChange: (file: File) => void;
  onPasswordChange: (value: string) => void;
  isDragOver: boolean;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onFileError: (message: string) => void;
  providerName?: string;
  useSystemCertificate?: boolean;
}

export function CertificateStep({
  certificate,
  certificatePassword,
  onCertificateChange,
  onPasswordChange,
  isDragOver,
  onDragOver,
  onDragLeave,
  onDrop,
  onFileError,
  providerName,
  useSystemCertificate
}: CertificateStepProps) {
  const handleFileUpload = (file: File) => {
    if (file.name.toLowerCase().endsWith('.pfx')) {
      onCertificateChange(file);
    } else {
      onFileError('Invalid file type. Only .pfx files are allowed.');
    }
  };

  if (useSystemCertificate) {
    return (
      <div style={{
        marginBottom: '1rem',
        padding: '1rem',
        backgroundColor: '#f3f4f6',
        borderRadius: '0.5rem'
      }}>
        <p style={{ margin: 0, color: '#374151', fontSize: '0.875rem' }}>
          The Organization uses system certificate
        </p>
      </div>
    );
  }

  return (
    <>
      <div style={{
        marginBottom: '1rem',
        padding: '1rem',
        backgroundColor: '#f3f4f6',
        borderRadius: '0.5rem'
      }}>
        <p style={{ margin: 0, color: '#374151', fontSize: '0.875rem' }}>
          Upload the certificate file (.pfx) provided by {providerName || 'the bank'}.
        </p>
      </div>
      <FormGroup>
        <Label>Certificate File (.pfx) *</Label>
        <FileUploadArea
          $isDragOver={isDragOver}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onClick={() => document.getElementById('certificate-file')?.click()}
        >
          <DocumentArrowUpIcon style={{ width: '3rem', height: '3rem', margin: '0 auto 1rem' }} />
          {certificate ? (
            <div>
              <p><strong>{certificate.name}</strong></p>
              <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>Click to replace</p>
            </div>
          ) : (
            <div>
              <p>Drag and drop your .pfx certificate file here</p>
              <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>or click to browse</p>
            </div>
          )}
        </FileUploadArea>
        <FileInput
          id="certificate-file"
          type="file"
          accept=".pfx"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileUpload(file);
          }}
        />
      </FormGroup>
      <FormGroup>
        <Label htmlFor="certificatePassword">Certificate Password (Optional)</Label>
        <Input
          id="certificatePassword"
          type="password"
          value={certificatePassword}
          onChange={(e) => onPasswordChange(e.target.value)}
          placeholder="Enter certificate password (leave empty if none)"
        />
        <small style={{ color: '#6b7280', marginTop: '0.5rem', display: 'block' }}>
          The password used to protect your .pfx certificate file. Leave empty if the certificate is not password protected.
        </small>
      </FormGroup>
    </>
  );
}